'use client'

import * as React from 'react'
import { MapPin, X, ChevronDown, Search, Check, Loader2 } from 'lucide-react'
import { cn } from '@/lib/utils'
import { api } from '@/lib/api'
import type { Location } from '@/lib/types'
import { useI18n } from '@/lib/i18n'

interface LocationSelectorProps {
  selectedCity: string
  selectedDistricts: string[]
  onDistrictsChange: (districts: string[]) => void
  className?: string
}

export function LocationSelector({
  selectedCity,
  selectedDistricts,
  onDistrictsChange,
  className,
}: LocationSelectorProps) {
  const { t } = useI18n()
  const [isOpen, setIsOpen] = React.useState(false)
  const [searchQuery, setSearchQuery] = React.useState('')
  const [locations, setLocations] = React.useState<Location[]>([])
  const [isLoading, setIsLoading] = React.useState(false)
  const dropdownRef = React.useRef<HTMLDivElement>(null)
  const searchInputRef = React.useRef<HTMLInputElement>(null)
  const debounceRef = React.useRef<NodeJS.Timeout>(null)

  // Fetch locations only when user types a search query
  React.useEffect(() => {
    if (!isOpen || !searchQuery.trim()) {
      setLocations([])
      return
    }

    // Debounce search requests
    if (debounceRef.current) {
      clearTimeout(debounceRef.current)
    }

    debounceRef.current = setTimeout(async () => {
      setIsLoading(true)
      try {
        const results = await api.getLocations(selectedCity, searchQuery, 15)
        setLocations(results)
      } catch (error) {
        console.error('Failed to fetch locations:', error)
        setLocations([])
      } finally {
        setIsLoading(false)
      }
    }, 300)

    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current)
      }
    }
  }, [isOpen, searchQuery, selectedCity])

  // Focus search input when dropdown opens
  React.useEffect(() => {
    if (isOpen && searchInputRef.current) {
      searchInputRef.current.focus()
    }
  }, [isOpen])

  // Close dropdown when clicking outside
  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false)
        setSearchQuery('')
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const toggleDistrict = (district: string) => {
    if (selectedDistricts.includes(district)) {
      onDistrictsChange(selectedDistricts.filter(d => d !== district))
    } else {
      onDistrictsChange([...selectedDistricts, district])
    }
  }

  const removeDistrict = (district: string, e: React.MouseEvent) => {
    e.stopPropagation()
    onDistrictsChange(selectedDistricts.filter(d => d !== district))
  }

  return (
    <div className={cn('relative', className)} ref={dropdownRef}>
      {/* District selector button with chips inside */}
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className={cn(
          "inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full",
          // Solid frosted surface — matches the City chip; the old 10%
          // primary tint disappeared over the hero illustration.
          "bg-card/80 backdrop-blur-sm border border-border/70 shadow-sm text-primary text-sm font-medium",
          "hover:bg-card transition-colors",
          "focus:outline-none focus:ring-2 focus:ring-primary/30"
        )}
      >
        <MapPin className="w-3.5 h-3.5 text-muted-foreground" />
        <span className="text-muted-foreground font-normal">{t('search.districtLabel', 'District:')}</span>
        
        {selectedDistricts.length === 0 ? (
          <span>{t('search.districtAll', 'All')}</span>
        ) : (
          <span className="inline-flex items-center gap-1">
            {selectedDistricts.map((district) => (
              <span
                key={district}
                className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded bg-primary/20 text-primary text-xs font-medium"
              >
                {district}
                <span
                  role="button"
                  tabIndex={0}
                  onClick={(e) => removeDistrict(district, e)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      removeDistrict(district, e as any)
                    }
                  }}
                  className="ml-0.5 hover:bg-primary/30 rounded-full p-0.5 cursor-pointer"
                  aria-label={`${t('search.removeFilter', 'Remove')} ${district}`}
                >
                  <X className="w-2.5 h-2.5" />
                </span>
              </span>
            ))}
          </span>
        )}
        
        <ChevronDown className={cn(
          "w-3.5 h-3.5 transition-transform",
          isOpen && "rotate-180"
        )} />
      </button>

      {/* Dropdown */}
      {isOpen && (
        <div className="absolute top-full left-1/2 -translate-x-1/2 mt-1 z-50 min-w-[220px] rounded-lg bg-popover border border-border shadow-lg overflow-hidden">
          {/* Search input */}
          <div className="p-2 border-b border-border">
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <input
                ref={searchInputRef}
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder={t('search.searchDistricts', 'Search districts...')}
                className={cn(
                  "w-full pl-8 pr-3 py-1.5 text-sm rounded-md",
                  "bg-muted/50 border border-border",
                  "focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary"
                )}
              />
            </div>
          </div>

          {/* Locations list */}
          <div className="max-h-[240px] overflow-y-auto">
            {!searchQuery.trim() ? (
              <div className="px-3 py-4 text-sm text-muted-foreground text-center">
                {t('search.typeToSearchDistricts', 'Type to search districts...')}
              </div>
            ) : isLoading ? (
              <div className="flex items-center justify-center py-4">
                <Loader2 className="w-5 h-5 text-muted-foreground animate-spin" />
              </div>
            ) : locations.length === 0 ? (
              <div className="px-3 py-4 text-sm text-muted-foreground text-center">
                {t('search.noDistrictsFound', 'No districts found')}
              </div>
            ) : (
              locations.map((location, index) => {
                // Handle edge case where name might be an object from ES completion field
                let nameValue = location.name
                if (typeof nameValue === 'object' && nameValue !== null) {
                  const input = (nameValue as any).input
                  nameValue = Array.isArray(input) ? input[0] : input || ''
                }
                const displayName = nameValue || location.district
                const isSelected = selectedDistricts.includes(displayName)
                
                return (
                  <button
                    key={`${displayName}-${index}`}
                    type="button"
                    onClick={() => toggleDistrict(displayName)}
                    className={cn(
                      "w-full flex items-center gap-2 px-3 py-2 text-sm text-left",
                      "hover:bg-accent transition-colors",
                      isSelected && "bg-primary/5"
                    )}
                  >
                    <div className={cn(
                      "w-4 h-4 rounded border flex items-center justify-center flex-shrink-0",
                      isSelected 
                        ? "bg-primary border-primary text-primary-foreground" 
                        : "border-border"
                    )}>
                      {isSelected && <Check className="w-3 h-3" />}
                    </div>
                    <span className={cn(
                      "flex-1 truncate",
                      isSelected && "font-medium text-primary"
                    )}>
                      {displayName}
                    </span>
                    {location.type && location.type !== 'district' && (
                      <span className="text-xs text-muted-foreground">
                        {location.type}
                      </span>
                    )}
                  </button>
                )
              })
            )}
          </div>

          {/* Clear all button */}
          {selectedDistricts.length > 0 && (
            <div className="p-2 border-t border-border">
              <button
                type="button"
                onClick={() => onDistrictsChange([])}
                className="w-full px-3 py-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors"
              >
                {t('search.clearAll', 'Clear all')} ({selectedDistricts.length})
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

